import { useEffect, useMemo, useRef, useState } from 'react'
import { geoIdentity, geoPath } from 'd3-geo'
import type { Feature, FeatureCollection, Geometry } from 'geojson'
import {
  AlertTriangle,
  Layers,
  Map as MapIcon,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  type LucideIcon,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { colorHex } from '../../lib/reporteColor'
import { parseFidelizacionError } from '../../hooks/useFidelizacionError'
import { parseNumeric } from './palette'
import { formatNumero } from '../../lib/paisMapa'

// Geojson por defecto: provincias de Ecuador. Otros países (ej. Perú) se pasan
// vía la prop `geojsonUrl`. El geojson debe seguir el mismo esquema: features
// con `properties.shapeName` + geometría en CRS84.
const DEFAULT_GEOJSON_URL = '/geo/ecuador-provincias.geojson'
// Región que se dibuja como recuadro (inset) separado. Ecuador → Galápagos.
// Países sin inset (Perú) pasan `insetName={null}`.
const DEFAULT_INSET_NAME = 'galapagos'

const VIEWBOX_W = 460
const VIEWBOX_H = 340

const INSET_W = 110
const INSET_H = 78
const INSET_PADDING = 8
// Con inset: el continental ocupa la columna izquierda y el recuadro va a la
// derecha. Sin inset: el continental usa todo el ancho disponible.
const MAP_AREA_W_INSET = VIEWBOX_W - INSET_W - 2 * INSET_PADDING - 4
const MAP_AREA_W_FULL = VIEWBOX_W - 2 * INSET_PADDING
const MAP_AREA_H = VIEWBOX_H - 2 * INSET_PADDING
const INSET_X = VIEWBOX_W - INSET_W - INSET_PADDING
const INSET_Y = VIEWBOX_H - INSET_H - INSET_PADDING
const CLIP_ID = 'mapa-regiones-continental-clip'

const ZOOM_MIN = 1
const ZOOM_MAX = 12
const ZOOM_STEP = 1.4

const COLOR_TONES = ['200', '300', '400', '500', '600', '700', '800'] as const

interface RegionProps {
  shapeName: string
}

type RegionFeature = Feature<Geometry, RegionProps>
type RegionesCollection = FeatureCollection<Geometry, RegionProps>

export interface MapaDatum {
  label: string
  value: number | string
}

interface Props {
  title: string
  data: MapaDatum[]
  colorNombre: string
  isLoading?: boolean
  error?: unknown
  icon?: LucideIcon
  formatValue?: (v: number) => string
  /** Indicador compacto opcional renderizado a la derecha del título. */
  headerStat?: { value: number | string; label: string }
  /** URL del geojson a dibujar. Default: provincias de Ecuador. */
  geojsonUrl?: string
  /** Nombre de la región a dibujar como inset. `null` = sin inset (ej. Perú). */
  insetName?: string | null
  /**
   * Si se provee, muestra un botón en la cabecera para alternar el nivel del
   * mapa (ej. departamentos ↔ distritos). `toggleNivelLabel` es su texto.
   */
  onToggleNivel?: () => void
  toggleNivelLabel?: string
  /**
   * Relación de aspecto (CSS `aspect-ratio`) del contenedor del mapa, ej. `"460 / 170"`
   * para reducir el alto a la mitad. Default: el aspecto natural del viewBox.
   */
  aspectRatio?: string
  /** Id de configuración para el formateo de números según el país del reporte. */
  configId?: number
}

function stripDiacritics(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '')
}

function normalizeKey(s: string): string {
  return stripDiacritics(s).toLowerCase().trim()
}

function useGeoJSON(url: string) {
  const [data, setData] = useState<RegionesCollection | null>(null)
  const [error, setError] = useState<unknown>(null)
  useEffect(() => {
    let active = true
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json() as Promise<RegionesCollection>
      })
      .then((j) => {
        if (active) setData(j)
      })
      .catch((e) => {
        if (active) setError(e)
      })
    return () => {
      active = false
    }
  }, [url])
  return { data, error }
}

interface Bucket {
  features: RegionFeature[]
  collection: RegionesCollection
}

function splitInset(
  fc: RegionesCollection,
  insetName: string | null,
): {
  continental: Bucket
  inset: Bucket
} {
  const continental: RegionFeature[] = []
  const inset: RegionFeature[] = []
  const insetKey = insetName ? normalizeKey(insetName) : null
  for (const f of fc.features) {
    if (insetKey && normalizeKey(f.properties.shapeName) === insetKey) {
      inset.push(f)
    } else {
      continental.push(f)
    }
  }
  return {
    continental: {
      features: continental,
      collection: { type: 'FeatureCollection', features: continental } as RegionesCollection,
    },
    inset: {
      features: inset,
      collection: { type: 'FeatureCollection', features: inset } as RegionesCollection,
    },
  }
}

function buildValueByRegion(data: MapaDatum[]): {
  values: Map<string, number>
  max: number
  total: number
} {
  const values = new Map<string, number>()
  let max = 0
  let total = 0
  for (const d of data) {
    const key = normalizeKey(d.label)
    const n = parseNumeric(d.value)
    values.set(key, (values.get(key) ?? 0) + n)
    total += n
  }
  for (const v of values.values()) if (v > max) max = v
  return { values, max, total }
}

function fillFor(value: number, max: number, colorNombre: string): string {
  if (max <= 0 || value <= 0) {
    // sin datos: gris suave
    return 'hsl(var(--muted))'
  }
  // Escala basada en raíz para no aplastar valores pequeños
  const ratio = Math.sqrt(value / max)
  const idx = Math.min(COLOR_TONES.length - 1, Math.max(0, Math.round(ratio * (COLOR_TONES.length - 1))))
  return colorHex(colorNombre, COLOR_TONES[idx])
}

interface Transform {
  k: number
  x: number
  y: number
}

const IDENTITY: Transform = { k: 1, x: 0, y: 0 }

function clampTransform(t: Transform): Transform {
  const k = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, t.k))
  // Mantener el contenido dentro del viewBox: en escala k, los traslados válidos
  // están en [-(k-1)*W, 0] (igual para Y).
  const minX = -(k - 1) * VIEWBOX_W
  const minY = -(k - 1) * VIEWBOX_H
  return {
    k,
    x: Math.max(minX, Math.min(0, t.x)),
    y: Math.max(minY, Math.min(0, t.y)),
  }
}

function zoomAt(t: Transform, factor: number, px: number, py: number): Transform {
  const newK = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, t.k * factor))
  const ratio = newK / t.k
  return clampTransform({
    k: newK,
    x: px - (px - t.x) * ratio,
    y: py - (py - t.y) * ratio,
  })
}

interface TooltipState {
  x: number
  y: number
  nombre: string
  valor: number
  visible: boolean
}

export function MapaRegiones({
  title,
  data,
  colorNombre,
  isLoading,
  error,
  icon: Icon = MapIcon,
  formatValue,
  headerStat,
  geojsonUrl = DEFAULT_GEOJSON_URL,
  insetName = DEFAULT_INSET_NAME,
  aspectRatio = `${VIEWBOX_W} / ${VIEWBOX_H}`,
  onToggleNivel,
  toggleNivelLabel,
  configId,
}: Props) {
  const { data: geo, error: geoError } = useGeoJSON(geojsonUrl)
  const svgRef = useRef<SVGSVGElement>(null)
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null)
  const [transform, setTransform] = useState<Transform>(IDENTITY)
  const [tooltip, setTooltip] = useState<TooltipState>({
    x: 0,
    y: 0,
    nombre: '',
    valor: 0,
    visible: false,
  })

  const { values, max } = useMemo(() => buildValueByRegion(data), [data])

  const split = useMemo(() => (geo ? splitInset(geo, insetName) : null), [geo, insetName])

  const hasInset = !!split && split.inset.features.length > 0
  const mapAreaW = hasInset ? MAP_AREA_W_INSET : MAP_AREA_W_FULL

  const continentalPath = useMemo(() => {
    if (!split) return null
    // geoIdentity().reflectY(true) evita los artifacts de preclip del antimeridiano
    // que mete geoMercator(). Para un país chico la diferencia visual es nula.
    // fitExtent restringe el dibujo al área disponible; con inset queda la columna
    // izquierda, sin inset usa todo el ancho.
    const proj = geoIdentity().reflectY(true).fitExtent(
      [
        [INSET_PADDING, INSET_PADDING],
        [INSET_PADDING + mapAreaW, INSET_PADDING + MAP_AREA_H],
      ],
      split.continental.collection,
    )
    return geoPath(proj)
  }, [split, mapAreaW])

  const insetPath = useMemo(() => {
    if (!split || split.inset.features.length === 0) return null
    const proj = geoIdentity().reflectY(true).fitSize(
      [INSET_W - 8, INSET_H - 22],
      split.inset.collection,
    )
    return geoPath(proj)
  }, [split])

  const fmt = (v: number) => (formatValue ? formatValue(v) : formatNumero(v, configId ?? 0))

  // Wheel debe atachearse como no-pasivo para poder llamar preventDefault.
  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    const handler = (e: WheelEvent) => {
      e.preventDefault()
      const rect = svg.getBoundingClientRect()
      const px = ((e.clientX - rect.left) / rect.width) * VIEWBOX_W
      const py = ((e.clientY - rect.top) / rect.height) * VIEWBOX_H
      const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP
      setTransform((t) => zoomAt(t, factor, px, py))
    }
    svg.addEventListener('wheel', handler, { passive: false })
    return () => svg.removeEventListener('wheel', handler)
  }, [])

  const onMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (e.button !== 0) return
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      ox: transform.x,
      oy: transform.y,
    }
  }

  const onMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const drag = dragRef.current
    const svg = svgRef.current
    if (!drag || !svg) return
    const rect = svg.getBoundingClientRect()
    const scaleX = VIEWBOX_W / rect.width
    const scaleY = VIEWBOX_H / rect.height
    const dx = (e.clientX - drag.x) * scaleX
    const dy = (e.clientY - drag.y) * scaleY
    setTransform((t) => clampTransform({ k: t.k, x: drag.ox + dx, y: drag.oy + dy }))
  }

  const onMouseUp = () => {
    dragRef.current = null
  }

  const zoomBy = (factor: number) => {
    setTransform((t) => zoomAt(t, factor, VIEWBOX_W / 2, VIEWBOX_H / 2))
  }

  const reset = () => setTransform(IDENTITY)

  // Header común
  const renderHeader = (extra?: React.ReactNode) => (
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {headerStat && (
          <span className="ml-2 inline-flex items-baseline gap-1 rounded-md border border-gray-200 dark:border-gray-700 bg-muted/40 px-2 py-0.5 text-xs">
            <span className="font-semibold text-black dark:text-white">{headerStat.value}</span>
            <span className="text-muted-foreground">{headerStat.label}</span>
          </span>
        )}
      </div>
      {extra}
    </CardHeader>
  )

  if (isLoading || (!geo && !geoError && !error)) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700 h-full">
        {renderHeader()}
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    )
  }

  if (error || geoError) {
    const parsed = parseFidelizacionError(error ?? geoError)
    return (
      <Card className="border border-gray-300 dark:border-gray-700 h-full">
        {renderHeader()}
        <CardContent>
          <div className="flex flex-col items-center justify-center gap-2 h-[300px] text-center px-4">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            <p className="text-sm font-medium">{parsed.title}</p>
            <p className="text-xs text-muted-foreground">{parsed.message}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!continentalPath || !split) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700 h-full">
        {renderHeader()}
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground text-sm">
            Sin datos disponibles
          </div>
        </CardContent>
      </Card>
    )
  }

  const onRegionEnter = (
    e: React.MouseEvent<SVGPathElement>,
    feature: RegionFeature,
  ) => {
    const svg = svgRef.current
    if (!svg) return
    const rect = svg.getBoundingClientRect()
    const valor = values.get(normalizeKey(feature.properties.shapeName)) ?? 0
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      nombre: feature.properties.shapeName,
      valor,
      visible: true,
    })
  }

  const onRegionLeave = () => setTooltip((t) => ({ ...t, visible: false }))

  const renderFeature = (
    feature: RegionFeature,
    pathGen: ReturnType<typeof geoPath>,
    interactive: boolean,
  ) => {
    const d = pathGen(feature)
    if (!d) return null
    const valor = values.get(normalizeKey(feature.properties.shapeName)) ?? 0
    const fill = fillFor(valor, max, colorNombre)
    return (
      <path
        key={feature.properties.shapeName}
        d={d}
        fill={fill}
        strokeWidth={interactive ? 0.5 / transform.k : 0.4}
        vectorEffect="non-scaling-stroke"
        className={`stroke-gray-400 dark:stroke-gray-600 ${interactive ? 'cursor-pointer transition-opacity hover:opacity-80' : ''}`}
        onMouseEnter={interactive ? (e) => onRegionEnter(e, feature) : undefined}
        onMouseMove={interactive ? (e) => onRegionEnter(e, feature) : undefined}
        onMouseLeave={interactive ? onRegionLeave : undefined}
      />
    )
  }

  // Leyenda: 5 muestras del rango
  const legendItems = COLOR_TONES.map((t) => colorHex(colorNombre, t))

  // Nombre a mostrar en el recuadro del inset (ej. "Galápagos").
  const insetLabel = hasInset ? split.inset.features[0].properties.shapeName : ''

  return (
    <Card className="border border-gray-300 dark:border-gray-700 h-full">
      {renderHeader(
        <div className="flex items-center gap-1">
          {onToggleNivel && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs"
              onClick={onToggleNivel}
            >
              <Layers className="h-4 w-4" />
              {toggleNivelLabel}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => zoomBy(ZOOM_STEP)}
            aria-label="Acercar"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => zoomBy(1 / ZOOM_STEP)}
            aria-label="Alejar"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={reset}
            aria-label="Restablecer"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>,
      )}
      <CardContent>
        <div className="relative w-full" style={{ aspectRatio }}>
          <svg
            ref={svgRef}
            viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
            className="absolute inset-0 h-full w-full select-none touch-none"
            style={{ cursor: dragRef.current ? 'grabbing' : 'grab' }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={() => {
              onMouseUp()
              onRegionLeave()
            }}
            role="img"
            aria-label={title}
          >
            <defs>
              <clipPath id={CLIP_ID}>
                <rect
                  x={INSET_PADDING}
                  y={INSET_PADDING}
                  width={mapAreaW}
                  height={MAP_AREA_H}
                />
              </clipPath>
            </defs>
            <g clipPath={`url(#${CLIP_ID})`}>
              <g transform={`translate(${transform.x} ${transform.y}) scale(${transform.k})`}>
                {split.continental.features.map((f) =>
                  renderFeature(f, continentalPath, true),
                )}
              </g>
            </g>

            {/* Inset (ej. Galápagos): queda fijo, fuera del transform */}
            {insetPath && hasInset && (
              <g>
                <rect
                  x={INSET_X}
                  y={INSET_Y}
                  width={INSET_W}
                  height={INSET_H}
                  rx={6}
                  ry={6}
                  fill="hsl(var(--background))"
                  stroke="currentColor"
                  strokeOpacity={0.15}
                  strokeWidth={0.6}
                />
                <text
                  x={INSET_X + 6}
                  y={INSET_Y + 12}
                  fontSize={9}
                  className="fill-muted-foreground"
                >
                  {insetLabel}
                </text>
                <g transform={`translate(${INSET_X + 4} ${INSET_Y + 18})`}>
                  {split.inset.features.map((f) => renderFeature(f, insetPath, true))}
                </g>
              </g>
            )}
          </svg>

          {tooltip.visible && (
            <div
              className="pointer-events-none absolute z-10 rounded-md border border-border bg-background px-2 py-1 text-xs shadow-md"
              style={{
                left: tooltip.x + 12,
                top: tooltip.y + 12,
                transform:
                  tooltip.x > (svgRef.current?.getBoundingClientRect().width ?? VIEWBOX_W) - 140
                    ? 'translateX(-100%) translateX(-24px)'
                    : undefined,
              }}
            >
              <div className="font-medium">{tooltip.nombre}</div>
              <div className="text-muted-foreground">{fmt(tooltip.valor)}</div>
            </div>
          )}
        </div>

        {/* Leyenda */}
        <div className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>0</span>
          <div className="flex h-2 flex-1 overflow-hidden rounded-full">
            {legendItems.map((hex, i) => (
              <div key={i} className="flex-1" style={{ background: hex }} />
            ))}
          </div>
          <span>{fmt(max)}</span>
        </div>
      </CardContent>
    </Card>
  )
}
